import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';

import AssignmentIcon from '@material-ui/icons/Assignment';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const now = DateTime.local();

const AdvancedListItem = (props) => {

    const { election } = props;

    const proposals_start_at = DateTime.fromISO(election.proposals_start_at);
    const proposals_end_at = DateTime.fromISO(election.proposals_end_at);
    const recommendations_start_at = DateTime.fromISO(election.recommendations_start_at);
    const recommendations_end_at = DateTime.fromISO(election.recommendations_end_at);
    const voting_starts_at = DateTime.fromISO(election.voting_starts_at);
    const voting_ends_at = DateTime.fromISO(election.voting_ends_at);

    const proposals_active = () => {

    }

    return (
        <ListItem
            key={election.id}
            divider
            component={Link}
            >
            <ListItemIcon>
                <AssignmentIcon />
            </ListItemIcon>
            <ListItemText
                primary={election.title}
                secondary={election.description}
            />
        </ListItem>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
}));

export default function Elections(props) {

    const { variant, variantTitle } = props;
    const classes = useStyles();
    const api = useApi();

    const [ elections, setElections ] = useState([]);
    const [ electionStatus, setElectionStatus ] = useState(`["active", "upcoming"]`);

    const [ loading, setLoading ] = useState(false);

    const getElections = () => {
        setLoading(true);

        api.get(`/election/${variant}`).then(res => setElections(res.elections)).finally(() => setLoading(false));
    }

    useEffect(() => {
        getElections();
    }, []);

    const handleElectionStatus = event => {
        setElectionStatus(event.target.value);
    }

    const getActiveSecondary = (election) => {

        const s1 = (variant === 'voting') ? '' : 's';
        const s2 = (variant === 'voting') ? 's' : '';

        const st = DateTime.fromISO(election[`${variant}${s1}_end${s2}_at`]);

        return st.isValid ? (
            <Typography variant="subtitle2" color='secondary'>
                Ends {st.toLocaleString(DateTime.DATETIME_FULL)} ({st.toRelative()})
            </Typography>
        ) : (
            <Typography variant="subtitle2" color='secondary'>
                Continuous
            </Typography>
        )
    }

    const getUpcomingSecondary = (election) => {

        const s1 = (variant === 'voting') ? '' : 's';
        const s2 = (variant === 'voting') ? 's' : '';

        const st = DateTime.fromISO(election[`${variant}${s1}_start${s2}_at`]);
        const et = DateTime.fromISO(election[`${variant}${s1}_end${s2}_at`]);
        const est = DateTime.fromISO(election[`starts_at`]);

        return st.isValid ? (
            <Typography variant="subtitle2" color='secondary'>
                {variantTitle} begin{s2} {st.toLocaleString(DateTime.DATETIME_FULL)} ({st.toRelative()}) to {et.toLocaleString(DateTime.DATETIME_MED)}
            </Typography>
        ) : (
            <Typography variant="subtitle2" color='secondary'>
                {variantTitle} begin{s2} {est.toLocaleString(DateTime.DATETIME_FULL)} ({est.toRelative()})
            </Typography>
        )
    }

    const esArray = JSON.parse(electionStatus);

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid
                    container
                    justify="space-between"
                    alignItems="center"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography
                            variant="h4"
                            component="h2"
                            >
                            {variantTitle}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            id="input-select-election-period"
                            select
                            value={electionStatus}
                            onChange={handleElectionStatus}
                            variant="outlined"
                            >
                            <MenuItem value={`["active", "upcoming", "past"]`}>
                                All {variantTitle}
                            </MenuItem>
                            <MenuItem value={`["upcoming"]`}>
                                Upcoming
                            </MenuItem>
                            <MenuItem value={`["active"]`}>
                                Active
                            </MenuItem>
                            <MenuItem value={`["active", "upcoming"]`}>
                                Active & Upcoming
                            </MenuItem>
                            <MenuItem value={`["past"]`}>
                                Past
                            </MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
                {esArray.includes('active') &&
                    <div className={classes.marginBottom}>
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                            Active {variantTitle}
                        </Typography>
                        <Divider/>
                        {(elections && elections.active && elections.active.length) ? (
                            <List className={classes.list}>
                                {elections.active.map(election => (
                                    <ListItem
                                        key={election.id}
                                        divider
                                        button
                                        component={Link}
                                        to={`/elections/${election.id}/${variant}`}
                                        >
                                        <ListItemIcon>
                                            <AssignmentIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" color="textSecondary">
                                                    <strong>{election.formatted_name}</strong> - {election.description}
                                                </Typography>
                                            }
                                            secondary={getActiveSecondary(election)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                No Active {variantTitle} at this time. Check back soon.
                            </Typography>
                        )}
                    </div>
                }
                {esArray.includes('upcoming') &&
                    <div className={classes.marginBottom}>
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                            Upcoming {variantTitle}
                        </Typography>
                        <Divider/>
                        {(elections && elections.upcoming && elections.upcoming.length) ? (
                            <List className={classes.list}>
                                {elections.upcoming.map(election => (
                                    <ListItem
                                        key={election.id}
                                        divider
                                        >
                                        <ListItemIcon>
                                            <AssignmentIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" color="textSecondary">
                                                    <strong>{election.formatted_name}</strong> - {election.description}
                                                </Typography>
                                            }
                                            secondary={getUpcomingSecondary(election)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                No Upcoming {variantTitle} at this time. Check back soon.
                            </Typography>
                        )}
                    </div>
                }
                {esArray.includes('past') &&
                    <div className={classes.marginBottom}>
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                            Past {variantTitle}
                        </Typography>
                        <Divider/>
                        {(elections && elections.past && elections.past.length) ? (
                            <List className={classes.list}>
                                {elections.past.map(election => (
                                    <ListItem
                                        key={election.id}
                                        divider
                                        button
                                        component={Link}
                                        to={`/elections/${election.id}/${variant}`}
                                        >
                                        <ListItemIcon>
                                            <AssignmentIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle1" color="textSecondary">
                                                    <strong>{election.formatted_name}</strong>
                                                </Typography>
                                            }
                                            secondary={election.description}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle2"
                                className={classes.empty}
                                >
                                No Past {variantTitle} are available for review.
                            </Typography>
                        )}
                    </div>
                }
            </Paper>
        </div>
    );
}
